/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import {
  AbstractFormComponent,
  CommonActions,
  ContactDetailsDialogConfig,
  contactDetailsDialogRoute,
  FormActionPipelines,
  FormFlowState,
  PbHousePlanFinderDialogConfig,
  pbHousePlanFinderDialogRoute,
  PbHousePlanFinderListItem,
} from '@gh/builder-shared';
import { ContactType, validateSiteAddressInfo } from '@gh/builder-shared-data';
import { ActionFlow, AppRouter, getSingleNewSelectedEntity, UserContext } from '@gh/common-shared';
import { AutocompleteItem } from '@gh/common-shared-data';
import { MessageBasket } from '@gh/core-messages';
import { AutocompleteLoadMoreEvent, DialogService, TinymceSettings, tmSettingsBuilder } from '@gh/core-ui';
import { filter$, map$ } from '@gh/rx/operators';
import { JobQuickCreateResponse } from '../../';
import { JobQuickDetailsBusinessService } from '../../resources/job-quick-details-business.service';
import { JobCustomer, JobQuickDetails } from '../../resources/job.model';
import { JobQuickDetailsFormState } from './job-quick-details-form.service';

export type JobQuickDetailsSaveFlowState = FormFlowState<JobQuickDetails> & { saveResponse: JobQuickCreateResponse };

@Component({
  selector: 'gh-job-quick-details',
  template: require('./job-quick-details.component.html'),
  styles: [require('./job-quick-details.component.scss')],
  providers: [JobQuickDetailsFormState],
})
export class JobQuickDetailsComponent extends AbstractFormComponent<JobQuickDetails> implements OnInit {
  tmSettings: TinymceSettings;

  constructor(private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              public form: JobQuickDetailsFormState,
              private userContext: UserContext,
              private business: JobQuickDetailsBusinessService,
              private dialogs: DialogService,
              private router: AppRouter) {
    super();
  }

  get completeFlow(): ActionFlow<JobQuickDetailsSaveFlowState> {
    return this.pipelines.complete<JobQuickDetailsSaveFlowState>({
      successMessage: (state) => ({ id: 'msg_quick_job_created', params: { number: state.saveResponse.jobNumber } }),
      validate: this.commonActions.validate((state) => this.validateBeforeProcess(state.value)),
      execute: this.commonActions.async((state) => this.business.create(state.value).pipe(
        map$((saveResponse) => ({ ...state, saveResponse })))),
      finish: () => this.router.navigateByUrl('/job-admin/jobs'),
    });
  }

  ngOnInit(): void {
    this.form.build();

    this.tmSettings = tmSettingsBuilder().scayt().height(200).statusBar(false).build();
  }

  loadMoreCustomers(event: AutocompleteLoadMoreEvent): void {
    event.load(this.business.findCustomersByName(event.query));
  }

  loadMoreSalesPersons(event: AutocompleteLoadMoreEvent): void {
    event.load(this.business.findSalesPersonsByName(event.query));
  }

  loadMorePriceBooks(event: AutocompleteLoadMoreEvent): void {
    event.load(this.business.findPriceBooksByName(event.query));
  }

  loadMoreBaseDesigns(event: AutocompleteLoadMoreEvent): void {
    event.load(this.business.findAllBaseDesignsByName(event.query));
  }

  onCustomerChange(contact: JobCustomer): void {
    this.form.updateContact(contact);
  }

  onPriceBookChange(pb: AutocompleteItem): void {
    this.form.updatePriceBook(pb);
  }

  onOpenCustomer(): void {
    const { contact } = this.form;
    if (contact) {
      this.openContact(contact);
    }
  }

  onCreateCustomer(): void {
    this.openContact();
  }

  onSearchHousePlan(): void {
    const { priceBook, housePlan, isQqShow } = this.form;
    if (priceBook) {
      this.dialogs.openWithInput(pbHousePlanFinderDialogRoute, <PbHousePlanFinderDialogConfig>{
        multiselection: false,
        preselected: housePlan ? [housePlan.housePlanId] : [],
        priceBookId: priceBook.id,
        priceBookName: priceBook.name,
        filter: {
          isSalesEstimate: true,
          isQuickQuote: isQqShow,
        },
      }).afterClosed().pipe(
        filter$(Boolean),
        map$(getSingleNewSelectedEntity))
        .subscribe((hp: PbHousePlanFinderListItem) => {
          this.form.setHousePlan(hp);
        });
    }
  }

  onClearHousePlan(): void {
    this.form.clearHousePlan();
  }

  private openContact(openedContact?: JobCustomer): void {
    this.dialogs.openWithInput(contactDetailsDialogRoute, <ContactDetailsDialogConfig>{
      contactType: ContactType.Customer,
      contactId: openedContact ? openedContact.contactId : void 0,
      parentTitle: 'lbl_quick_job_create',
      entryPoint: 'quick-job',
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((contact: any) => {
        this.form.setContact(contact);
      });
  }

  private validateBeforeProcess(site: JobQuickDetails): Maybe<MessageBasket> {
    const errorId = validateSiteAddressInfo(site);
    return errorId ? MessageBasket.error(errorId) : void 0;
  }
}
